import { osnovaApi } from '../base';
import type { FieldRdPosition } from '@entities/field/types';
import type {
  ExportJob,
  KspProject,
  KspWorkspace,
  KspSummaryEnvelope,
  NotificationDelivery,
  KspCollectionPage,
  KspImport,
  KspFieldFront,
  KspRdDates,
  KspRdShift,
} from '@entities/ksp/types';

export interface KspMutation {
  projectId: string;
  path: string;
  method?: 'POST' | 'PATCH' | 'DELETE';
  body: Record<string, unknown>;
  key: string;
}
export const kspApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /** Полный небольшой справочник фронтов; ошибка страницы не превращается в неполный успех. */
    getKspFieldFronts: b.query<{ sites: KspFieldFront[] }, string>({
      async queryFn(projectId, api, _extra, baseQuery) {
        const sites: KspFieldFront[] = [];
        for (let offset = 0; ; offset += 200) {
          if (api.signal.aborted)
            return { error: { status: 'CUSTOM_ERROR', error: 'Загрузка фронтов отменена' } };
          const result = await baseQuery({
            url: `/agents/ksp/projects/${projectId}/field-fronts`,
            params: { limit: 200, offset },
          });
          if (result.error) return { error: result.error };
          const page = result.data as { sites: KspFieldFront[]; total: number };
          sites.push(...page.sites);
          if (page.sites.length < 200 || sites.length >= page.total) return { data: { sites } };
        }
      },
      providesTags: (_r, _e, id) => [
        { type: 'KspProject', id },
        'Field',
        'PtoVolume',
        'PtoRdRevision',
        'PtoRdDocument',
      ],
    }),
    getKspRdPositions: b.query<
      {
        positions: (FieldRdPosition & { document_code?: string; document_title?: string })[];
        total: number;
        limit: number;
        offset: number;
      },
      { projectId: string; field_site_id?: string; q?: string; limit?: number; offset?: number }
    >({
      query: ({ projectId, ...params }) => ({
        url: `/agents/ksp/projects/${projectId}/rd-positions`,
        params,
      }),
      providesTags: (_r, _e, { projectId }) => [
        { type: 'KspProject', id: projectId },
        'Field',
        'PtoVolume',
        'PtoRdRevision',
        'PtoRdDocument',
      ],
    }),
    getKspImport: b.query<KspImport, { projectId: string; id: string }>({
      query: ({ id }) => `/agents/ksp/imports/${id}`,
      providesTags: (_r, _e, { projectId }) => [{ type: 'KspProject', id: projectId }],
    }),
    uploadKspOriginal: b.mutation<
      { id: string },
      { projectId: string; file: File; key: string; expectedVersion: number }
    >({
      query: ({ projectId, file, key, expectedVersion }) => {
        const body = new FormData();
        body.append('file', file);
        body.append('profile', JSON.stringify({ expected_version: expectedVersion }));
        return {
          url: `/agents/ksp/projects/${projectId}/files`,
          method: 'POST',
          body,
          headers: { 'Idempotency-Key': key },
        };
      },
      invalidatesTags: (_r, _e, { projectId }) => [{ type: 'KspProject', id: projectId }],
    }),
    getKspDeliveries: b.query<{ items: NotificationDelivery[] }, string>({
      query: (id) => `/agents/ksp/alerts/${id}/deliveries`,
      providesTags: ['KspProject'],
    }),
    getKspProjects: b.query<{ items: KspProject[] }, void>({
      query: () => '/agents/ksp/projects',
      providesTags: ['KspProject'],
    }),
    getKspWorkspace: b.query<
      KspWorkspace,
      { projectId: string; versionId?: string; snapshotId?: string; period?: string }
    >({
      query: ({ projectId, ...args }) => ({
        url: `/agents/ksp/projects/${projectId}/workspace`,
        params: { version_id: args.versionId, snapshot_id: args.snapshotId, period: args.period },
      }),
      transformResponse: (response: KspWorkspace) => {
        const settings = response.settings;
        // Resolve aliases only from this response, including frozen history.
        // Never fill a historical setting from a previously cached live slice.
        if (!settings) return response;
        return {
          ...response,
          settings: {
            ...settings,
            daily_notice_time: settings.daily_notice_time ?? settings.notification_time ?? '',
            sender: settings.sender ?? settings.organization_name ?? '',
            customer: settings.customer ?? settings.customer_name ?? '',
            coverage: settings.coverage ?? settings.rd_coverage ?? 'unknown',
          },
        };
      },
      providesTags: (_r, _e, { projectId }) => [
        { type: 'KspProject', id: projectId },
        { type: 'KspSnapshot', id: projectId },
      ],
    }),
    getKspSummary: b.query<KspSummaryEnvelope, string>({
      query: (id) => `/agents/ksp/projects/${id}/summary`,
      providesTags: (_r, _e, id) => [{ type: 'KspSnapshot', id }],
    }),
    getKspCollection: b.query<
      KspCollectionPage,
      {
        projectId: string;
        collection: string;
        versionId?: string;
        snapshotId?: string;
        offset: number;
        limit: number;
      }
    >({
      query: ({ projectId, collection, versionId, snapshotId, offset, limit }) => ({
        url: `/agents/ksp/projects/${projectId}/collections/${collection}`,
        params: { version_id: versionId, snapshot_id: snapshotId, offset, limit },
      }),
      providesTags: (_r, _e, { projectId }) => [{ type: 'KspProject', id: projectId }],
    }),
    mutateKsp: b.mutation<Record<string, unknown>, KspMutation>({
      query: ({ path, method = 'POST', body, key }) => ({
        url: `/agents/ksp/${path}`,
        method,
        body,
        headers: { 'Idempotency-Key': key },
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'KspProject', id: projectId },
        { type: 'KspSnapshot', id: projectId },
        'ProgressObject',
        'Progress',
      ],
    }),
    importKsp: b.mutation<
      Record<string, unknown>,
      {
        projectId: string;
        file: File;
        kind: string;
        key: string;
        expectedVersion: number;
        dateFormat: string;
        separator: string;
      }
    >({
      query: ({ projectId, file, kind, key, expectedVersion, dateFormat, separator }) => {
        const body = new FormData();
        body.append('file', file);
        body.append('kind', kind);
        body.append('expected_version', String(expectedVersion));
        body.append(
          'profile',
          JSON.stringify({
            schema_version: 'ksp-1.0',
            kind,
            date_format: dateFormat || null,
            separator,
            expected_version: expectedVersion,
          }),
        );
        return {
          url: `/agents/ksp/projects/${projectId}/imports`,
          method: 'POST',
          body,
          headers: { 'Idempotency-Key': key },
        };
      },
      invalidatesTags: (_r, _e, { projectId }) => [{ type: 'KspProject', id: projectId }],
    }),
    /** Даты выдачи РД: реестр ПТО с кандидатами, подтверждения, связи, плановые даты. */
    getKspRdDates: b.query<KspRdDates, string>({
      query: (projectId) => `/agents/ksp/projects/${projectId}/rd-dates`,
      providesTags: (_r, _e, id) => [
        { type: 'KspProject', id },
        'PtoRdRevision',
        'PtoRdDocument',
      ],
    }),
    getKspRdShift: b.query<KspRdShift, { projectId: string; snapshot?: string }>({
      query: ({ projectId, snapshot }) => ({
        url: `/agents/ksp/projects/${projectId}/rd-dates/shift`,
        params: { snapshot },
      }),
      providesTags: (_r, _e, { projectId }) => [
        { type: 'KspProject', id: projectId },
        { type: 'KspSnapshot', id: projectId },
      ],
    }),
    uploadKspRdIssue: b.mutation<
      Record<string, unknown>,
      { projectId: string; file: File; key: string; expectedVersion: number }
    >({
      query: ({ projectId, file, key, expectedVersion }) => {
        const body = new FormData();
        body.append('file', file);
        body.append('profile', JSON.stringify({ expected_version: expectedVersion }));
        return {
          url: `/agents/ksp/projects/${projectId}/rd-dates/imports`,
          method: 'POST',
          body,
          headers: { 'Idempotency-Key': key },
        };
      },
      invalidatesTags: (_r, _e, { projectId }) => [{ type: 'KspProject', id: projectId }],
    }),
    getKspExport: b.query<ExportJob, string>({
      query: (id) => `/agents/ksp/exports/${id}`,
      providesTags: ['KspExport'],
    }),
    getProgressKspSummary: b.query<KspSummaryEnvelope, string>({
      query: (id) => ({ url: '/agents/progress/ksp-summary', params: { project_id: id } }),
      providesTags: (_r, _e, id) => [{ type: 'KspSnapshot', id }],
    }),
    getProgressKspPortfolio: b.query<
      {
        projects: { id: string; name: string; summary: KspSummaryEnvelope }[];
        physical_progress_pct: string | null;
        reasons: string[];
      },
      void
    >({
      query: () => '/agents/progress/portfolio-ksp',
      providesTags: ['KspSnapshot'],
    }),
  }),
});
export const {
  useGetKspImportQuery,
  useGetKspFieldFrontsQuery,
  useGetKspRdPositionsQuery,
  useUploadKspOriginalMutation,
  useGetKspProjectsQuery,
  useGetKspDeliveriesQuery,
  useGetKspWorkspaceQuery,
  useGetKspSummaryQuery,
  useLazyGetKspCollectionQuery,
  useMutateKspMutation,
  useImportKspMutation,
  useGetKspExportQuery,
  useGetKspRdDatesQuery,
  useGetKspRdShiftQuery,
  useUploadKspRdIssueMutation,
  useGetProgressKspSummaryQuery,
  useGetProgressKspPortfolioQuery,
} = kspApi;
